import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { paths } from "@/lib/api-types";
import { likeFilter } from "./filters";
import { adminQueryKeys } from "./queryKeys";
import {
  createListQueryInit,
  listQueryOptions,
} from "./hooks";
import type { SortOrder } from "./sort";

export type AdminTagListItem =
  paths["/api/admin/tags"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

type AdminTagsListQuery = {
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortOrder: SortOrder;
  filterText?: string;
};

export function useAdminTagsList(params: AdminTagsListQuery) {
  const filterText = params.filterText?.trim() ?? "";
  const filter = useMemo(
    () => (filterText !== "" ? likeFilter("name", filterText) : undefined),
    [filterText],
  );
  const init = createListQueryInit(
    { ...params, filter },
    "name",
  );
  return apiClient.useQuery("get", "/api/admin/tags", init, listQueryOptions);
}

export function useAdminTagCreate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/tags", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.tags.list,
      });
    },
  });
}

export function useAdminTagUpdate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("patch", "/api/admin/tags/{id}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.tags.list,
      });
    },
  });
}

export function useAdminTagDelete() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("delete", "/api/admin/tags/{id}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.tags.list,
      });
    },
  });
}
